import * as React from 'react'
import { Formik, FieldProps, Field } from 'formik'
import styled from 'styled-components'

import * as env from '@owl-nest/config'
import * as plume from '@ulule/owl-kit-components/next'
import { t, tc } from '@owl-nest/localize'

const ThirdPartyForm = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;

  ${plume.CheckboxField} {
    margin: 11px 0 20px;

    ${plume.styles.field.Title} {
      color: ${plume.COLORS.GREY_SHADE_3};
    }
  }

  ${plume.styles.copy.XS} {
    color: ${plume.COLORS.GREY_SHADE_3};
  }

  ${plume.styles.button.Button} {
    margin: 15px 0 30px;
  }
`

type ThirdPartyForm = {
  terms: boolean
  privacy: boolean
}

export type ThirdPartyService = 'facebook' | 'google'

type ThirdPartyModalProps = {
  open: boolean
  onClose: () => void
  service: ThirdPartyService
}

export function ThirdPartyModal({
  open,
  onClose,
  service,
}: ThirdPartyModalProps): React.ReactElement<ThirdPartyModalProps> {
  const configuration = getConfig(service)

  function onSubmit(): void {
    window.location.href = `${configuration.url}?next=${window.location.href}`
  }

  function validateTerms(value: boolean): string | undefined {
    if (value !== true) {
      return t('This field is required')
    }
  }

  function validatePrivacy(value: boolean): string | undefined {
    if (value !== true) {
      return t('This field is required')
    }
  }

  function getConfig(service: ThirdPartyService) {
    const configuration: Record<ThirdPartyService, { label: string; url: string | undefined }> = {
      facebook: {
        label: `I understand that Ulule will contact me via my Facebook account email`,
        url: env.FACEBOOK_LOGIN_URL,
      },
      google: {
        label: `I understand that Ulule will contact me via my Google's account email`,
        url: env.GOOGLE_LOGIN_URL,
      },
    }

    return configuration[service]
  }

  return (
    <plume.Modal open={open} onClose={onClose} closable={true}>
      <Formik<ThirdPartyForm> onSubmit={onSubmit} initialValues={{ terms: false, privacy: false }}>
        {({ handleSubmit }) => {
          return (
            <ThirdPartyForm noValidate onSubmit={handleSubmit}>
              <Field name="terms" validate={validateTerms}>
                {({ field, meta }: FieldProps<boolean>) => {
                  return (
                    <plume.CheckboxField
                      label={tc(
                        'I declare that I am over 18 years old and have read, understood and accepted the [link: Terms of Use]',
                        {
                          link: (
                            <plume.Link
                              href="https://www.ulule.com/about/terms/"
                              kind="secondary"
                              target="_blank"
                              rel="noopener"
                            />
                          ),
                        },
                      )}
                      checked={field.value}
                      onChange={field.onChange(field.name)}
                      onBlur={field.onBlur}
                      error={meta.touched ? meta.error : undefined}
                    />
                  )
                }}
              </Field>
              <Field name="privacy" validate={validatePrivacy}>
                {({ field, meta }: FieldProps<boolean>) => {
                  return (
                    <plume.CheckboxField
                      label={configuration.label}
                      checked={field.value}
                      onChange={field.onChange(field.name)}
                      onBlur={field.onBlur}
                      error={meta.touched ? meta.error : undefined}
                    />
                  )
                }}
              </Field>
              <plume.styles.button.Button>{t('Continue')}</plume.styles.button.Button>
              <plume.styles.copy.XS>
                {tc(
                  `The information you provide, with the exception of your password which is encrypted and cannot be read, is intended for the authorized staff of Ulule who is the data controller. Ulule collects your data to manage your user account, allow hosting and administration of fundraising campaigns, marketing and user relationship actions, as well as and fight against fraud, money laundering and terror financing. Depending on the purpose of each treatment, you have certain rights (right of opposition, right of access to your data, right to rectify and erase your data, right to define the fate of your data after your death). To exercise these rights, write to privacy@ulule.com or ULULE SAS, %(ululePostalAddress)s or, for certain rights, go directly to your account. For more details: [link: Ulule Privacy Policy].`,
                  {
                    ululePostalAddress: t('10 rue de Penthièvre, 75008 Paris, France'),
                    link: (
                      <plume.Link
                        href="https://www.ulule.com/about/privacy/"
                        kind="secondary"
                        target="_blank"
                        rel="noopener"
                      />
                    ),
                  },
                )}
              </plume.styles.copy.XS>
            </ThirdPartyForm>
          )
        }}
      </Formik>
    </plume.Modal>
  )
}
